import 'reflect-metadata';
import { injectable } from 'tsyringe'
import type { LoginResponse } from '../shared/types/LoginResponse'
import type { CreateChatResponse, CurrentUserResponse, UserSummary } from '../shared/types/ChatSummary'
import { appConfig } from '../config'
import {
  mockAiModels,
  mockInvoiceLink,
  mockLoginResponse,
  mockModels,
  mockTokenBalance
} from '../mocks/mockData'

export class ApiError extends Error {
  status?: number
  type?: string
  retryAfterSeconds?: number
  maxChats?: number

  constructor(message: string, options: { status?: number; type?: string; retryAfterSeconds?: number; maxChats?: number } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = options.status
    this.type = options.type
    this.retryAfterSeconds = options.retryAfterSeconds
    this.maxChats = options.maxChats
  }
}

@injectable()
export class ApiService {
    private baseTgUrl = '/api/Telegram'
    private baseModelUrl = '/api/Model'
    private baseTokensUrl = '/api/user-tokens'
    private baseUserUrl = '/api/User'
    private basePaymentUrl = '/api/Payment'
    private baseChatsUrl = '/api/Chats'
    private static authToken: string | null = null
    private static telegramInitData: string | null = null
  
  constructor() {}

  private normalizeLanguage(language?: string | null): 'ru' | 'en' {
    return language?.trim().toLowerCase().startsWith('ru') ? 'ru' : 'en'
  }

  private setAuthToken(token?: string | null) {
    ApiService.authToken = token?.trim() || null
    try {
      if (ApiService.authToken) {
        window.sessionStorage.setItem('tgappAuthToken', ApiService.authToken)
      } else {
        window.sessionStorage.removeItem('tgappAuthToken')
      }
    } catch {
      // Telegram WebView storage can be unavailable; in-memory token is enough for this session.
    }
  }

  private getAuthToken() {
    if (ApiService.authToken) {
      return ApiService.authToken
    }

    try {
      ApiService.authToken = window.sessionStorage.getItem('tgappAuthToken')
    } catch {
      ApiService.authToken = null
    }

    return ApiService.authToken
  }

  private setTelegramInitData(initData?: string | null) {
    ApiService.telegramInitData = initData?.trim() || null
    try {
      if (ApiService.telegramInitData) {
        window.sessionStorage.setItem('tgappInitData', ApiService.telegramInitData)
      } else {
        window.sessionStorage.removeItem('tgappInitData')
      }
    } catch {
      // Keep only the static copy if storage is blocked.
    }
  }

  private getTelegramInitData() {
    if (ApiService.telegramInitData) {
      return ApiService.telegramInitData
    }

    try {
      ApiService.telegramInitData = window.sessionStorage.getItem('tgappInitData')
    } catch {
      ApiService.telegramInitData = null
    }

    return ApiService.telegramInitData
  }

  private authHeaders(extra?: HeadersInit): HeadersInit {
    const headers: Record<string, string> = {}

    if (extra instanceof Headers) {
      extra.forEach((value, key) => {
        headers[key] = value
      })
    } else if (Array.isArray(extra)) {
      for (const [key, value] of extra) {
        headers[key] = value
      }
    } else if (extra) {
      Object.assign(headers, extra)
    }

    const token = this.getAuthToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    const telegramInitData = this.getTelegramInitData()
    if (telegramInitData) {
      headers['X-Telegram-Init-Data'] = telegramInitData
    }

    return headers
  }

  async validate(_initData: string): Promise<LoginResponse> {
    if (appConfig.useTelegramMock) {
      return {
        ...mockLoginResponse,
        user: {
          ...mockLoginResponse.user,
          telegramId: String(appConfig.mockTelegramUser.id),
          username: appConfig.mockTelegramUser.username ?? mockLoginResponse.user.username
        }
      }
    }

    this.setTelegramInitData(_initData)

    try {
      const res = await fetch(`${this.baseTgUrl}/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ initData: _initData }),
        credentials: 'include',
      })

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}))
        throw new Error(errorData.message || `Validation failed with status ${res.status}`)
      }
      
      const data: LoginResponse = await res.json()
      this.setAuthToken(data.token)
      return data
    } catch (error) {
      console.error('Validation API error:', error)
      throw error instanceof Error ? error : new Error('Network error during validation')
    }
  }

  async getWithAuth<T>(url: string, language?: string | null): Promise<T> {
    if (appConfig.useTelegramMock) {
      throw new Error(`Network request ${url} is unavailable in Telegram mock mode`)
    }

    try {
      const res = await fetch(url, {
        headers: this.authHeaders({
          'X-Language': this.normalizeLanguage(language),
        }),
        credentials: 'include'
      })

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}))
        throw new Error(errorData.message || `Request failed with status ${res.status}`)
      }
      
      return res.json()
    } catch (error) {
      console.error(`API request failed for ${url}:`, error)
      throw error instanceof Error ? error : new Error('Network error during request')
    }
  }

  async getModels(language?: string | null) {
    if (appConfig.useTelegramMock) {
      return { models: mockModels }
    }

    return this.getWithAuth<import('../shared/types/Model').ModelsResponse>(
      `${this.baseModelUrl}/models`,
      language,
    )
  }

  async getAiModels() {
    if (appConfig.useTelegramMock) {
      return mockAiModels
    }

    return this.getWithAuth<import('../shared/types/AiModel').AiModel[]>(
      `${this.baseModelUrl}/ai-models`
    )
  }

  async getTokenBalance() {
    if (appConfig.useTelegramMock) {
      return mockTokenBalance
    }

    const { balance } = await this.getWithAuth<{ balance: number }>(
      `${this.baseTokensUrl}/balance`
    )
    return balance
  }

  async getCurrentUser(): Promise<{ chatSummaries: UserSummary[] }> {
    if (appConfig.useTelegramMock) {
      return {
        chatSummaries: [
          {
            chatId: 1001,
            chatName: 'Mock first message',
            modelId: 1,
            isRoleplay: false,
            lastMessage: 'A saved mock conversation',
            updatedAt: new Date().toISOString(),
          },
          {
            chatId: 1002,
            chatName: 'Roleplay intro',
            modelId: 1,
            isRoleplay: true,
            lastMessage: 'A saved roleplay conversation',
            updatedAt: new Date(Date.now() - 3600_000).toISOString(),
          },
        ],
      }
    }

    const response = await this.getWithAuth<CurrentUserResponse>(`${this.baseUserUrl}/user`)
    return {
      chatSummaries: (response.user.chatSummaries ?? []).map((summary) => ({
        chatId: summary.chatId,
        chatName: summary.chatName ?? summary.name ?? `Chat #${summary.chatId}`,
        modelId: summary.modelId,
        isRoleplay: summary.isRoleplay ?? false,
        lastMessage: summary.lastMessage ?? summary.lastMessageText,
        updatedAt: summary.updatedAt ?? summary.lastMessageTimestamp,
      })),
    }
  }

  async createChat(modelId: number, isRoleplay: boolean): Promise<CreateChatResponse> {
    if (appConfig.useTelegramMock) {
      return {
        id: Math.floor(Date.now() / 1000),
        modelId,
        isRoleplay,
      }
    }

    try {
      const res = await fetch(`${this.baseChatsUrl}/chat`, {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ modelId, isRoleplay }),
        credentials: 'include',
      })

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}))
        throw new ApiError(errorData.Error || errorData.error || `Chat creation failed with status ${res.status}`, {
          status: res.status,
          type: errorData.Type || errorData.type,
          retryAfterSeconds: readNumber(errorData.RetryAfterSeconds ?? errorData.retryAfterSeconds),
          maxChats: readNumber(errorData.MaxChats ?? errorData.maxChats),
        })
      }

      return res.json()
    } catch (error) {
      console.error('Chat creation error:', error)
      throw error instanceof Error ? error : new Error('Network error during chat creation')
    }
  }

  async deleteChat(chatId: number): Promise<void> {
    if (appConfig.useTelegramMock) {
      return
    }

    try {
      const res = await fetch(`${this.baseChatsUrl}/delete/${chatId}`, {
        method: 'DELETE',
        headers: this.authHeaders(),
        credentials: 'include',
      })

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}))
        throw new Error(errorData.Error || errorData.error || `Chat deletion failed with status ${res.status}`)
      }
    } catch (error) {
      console.error('Chat deletion error:', error)
      throw error instanceof Error ? error : new Error('Network error during chat deletion')
    }
  }

  async setTelegramActiveChat(payload: { chatId: number; modelId: number; isRoleplay: boolean }): Promise<void> {
    if (appConfig.useTelegramMock) {
      return
    }

    try {
      const res = await fetch(`${this.baseTgUrl}/active-chat`, {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
        credentials: 'include',
      })

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}))
        throw new Error(errorData.Error || errorData.error || `Active chat selection failed with status ${res.status}`)
      }
    } catch (error) {
      console.error('Active chat selection error:', error)
      throw error instanceof Error ? error : new Error('Network error during active chat selection')
    }
  }

  async updateSettings(payload: { preferredLanguage: string | null; preferredAiModel: import('../shared/types/AiModel').AiModel | null }) {
    if (appConfig.useTelegramMock) {
      return
    }

    try {
      const res = await fetch(`${this.baseUserUrl}/settings`, {
        method: 'POST',
        headers: this.authHeaders({
          'Content-Type': 'application/json',
          'X-Language': this.normalizeLanguage(payload.preferredLanguage),
        }),
        body: JSON.stringify(payload),
        credentials: 'include'
      })

      if (!res.ok) {
        let message = 'Failed to save settings'
        try {
          const data: { Error?: string; error?: string } = await res.json()
          message = data.Error || data.error || message
        } catch {
          // ignore
        }
        throw new Error(message)
      }
    } catch (error) {
      console.error('Settings update error:', error)
      throw error instanceof Error ? error : new Error('Network error during settings update')
    }
  }

  async requestInvoice(tokensCount: number, provider: string): Promise<{ invoiceLink: string }> {
    if (appConfig.useTelegramMock) {
      return { invoiceLink: `${mockInvoiceLink}?provider=${provider}&amount=${tokensCount}` }
    }

    try {
      const res = await fetch(`${this.basePaymentUrl}/invoice`, {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ tokens: tokensCount, provider }),
        credentials: 'include'
      })
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}))
        throw new Error(errorData.message || `Invoice request failed with status ${res.status}`)
      }
      
      return res.json()
    } catch (error) {
      console.error('Invoice request error:', error)
      throw error instanceof Error ? error : new Error('Network error during invoice request')
    }
  }

  async requestPremiumInvoice(payload: { provider: string; amount: number; purpose: string }): Promise<{ invoiceLink: string }> {
    if (appConfig.useTelegramMock) {
      return { invoiceLink: `${mockInvoiceLink}?provider=${payload.provider}&premium=${payload.purpose}` }
    }

    try {
      const res = await fetch(`${this.basePaymentUrl}/premium/invoice`, {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
        credentials: 'include'
      })
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}))
        throw new Error(errorData.message || `Premium invoice request failed with status ${res.status}`)
      }
      
      return res.json()
    } catch (error) {
      console.error('Premium invoice request error:', error)
      throw error instanceof Error ? error : new Error('Network error during premium invoice request')
    }
  }

  async confirmPremium(payload: { provider: string; amount: number; purpose: string }): Promise<void> {
    if (appConfig.useTelegramMock) {
      return
    }

    try {
      const res = await fetch(`${this.basePaymentUrl}/premium/confirm`, {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
        credentials: 'include'
      })

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}))
        throw new Error(errorData.message || `Premium confirmation failed with status ${res.status}`)
      }
    } catch (error) {
      console.error('Premium confirmation error:', error)
      throw error instanceof Error ? error : new Error('Network error during premium confirmation')
    }
  }
}

function readNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return undefined
}
